import { Contract, RpcProvider, shortString, uint256, CallData, cairo } from 'starknet';
import CONTRACT_CLASS from '../contract_abi.json';

// Extract ABI from the contract class
const CONTRACT_ABI = CONTRACT_CLASS.abi;

class StarknetGameService {
  constructor() {
    // Starknet Sepolia Testnet RPC
    this.provider = new RpcProvider({
      nodeUrl: 'https://starknet-sepolia.public.blastapi.io/rpc/v0_7'
    });
    
    // Contract address - Deploy your contract and update this
    this.contractAddress = process.env.REACT_APP_STARKNET_CONTRACT_ADDRESS || '';
    
    this.contract = null;
    this.account = null;
    this.slashBuffer = [];
    this.BATCH_SIZE = 10; // Send slashes in batches of 10
    this.currentTokenId = 0;
    this.gameStartTime = null;
  }

  // Connect wallet using Argent X or Braavos
  async connectWallet() {
    try {
      console.log('🔵 Starting wallet connection...');
      console.log('🔍 Contract address:', this.contractAddress);
      
      // Check if Starknet wallet is available
      if (!window.starknet) {
        console.error('❌ window.starknet not found');
        throw new Error('Please install Braavos wallet');
      }

      console.log('✅ window.starknet found');
      console.log('🔍 Wallet info:', {
        id: window.starknet.id,
        name: window.starknet.name,
        version: window.starknet.version,
        isConnected: window.starknet.isConnected
      });

      // Request connection
      console.log('📞 Calling window.starknet.enable()...');
      const enableResult = await window.starknet.enable({ showModal: true });
      console.log('✅ Enable result:', enableResult);
      
      // Wait a moment for wallet to fully initialize
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Get the wallet account
      const walletAccount = window.starknet.account;
      
      if (!walletAccount) {
        console.error('❌ No account found after enable');
        console.log('window.starknet state:', {
          isConnected: window.starknet.isConnected,
          selectedAddress: window.starknet.selectedAddress,
          account: window.starknet.account
        });
        throw new Error('No account found. Please unlock your wallet.');
      }

      console.log('✅ Got wallet account:', {
        address: walletAccount.address,
        provider: !!walletAccount.provider,
        signer: !!walletAccount.signer
      });

      this.account = walletAccount;
      
      // Validate contract address
      if (!this.contractAddress) {
        console.error('❌ Contract address is missing!');
        throw new Error('Contract address not configured. Please check your .env file.');
      }

      // Initialize contract with account
      console.log('📝 Initializing contract...');
      this.contract = new Contract(
        CONTRACT_ABI,
        this.contractAddress,
        this.account
      );

      console.log('✅ Wallet connected successfully:', walletAccount.address);
      return {
        success: true,
        address: walletAccount.address
      };
    } catch (error) {
      console.error('❌ Failed to connect wallet:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      return {
        success: false,
        error: error.message || 'Failed to connect wallet'
      };
    }
  }

  // Disconnect wallet
  disconnectWallet() {
    this.account = null;
    this.contract = null;
    this.slashBuffer = [];
    console.log(' Wallet disconnected');
  }

  // Start a new game session
  startGameSession() {
    this.slashBuffer = [];
    this.gameStartTime = Date.now();
    this.currentTokenId = 0;
    console.log('Game session started');
  }

  // Record a slash (buffer for batch operation)
  recordSlash(slashData) {
    if (!this.account) {
      console.warn(' No wallet connected, slash not recorded on-chain');
      return;
    }

    // TODO: Implement slash recording on-chain
    // For now, just track locally for stats
    console.log('Slash recorded locally:', {
      type: slashData.isToken ? 'token' : 'bomb',
      points: slashData.points || 0,
      combo: slashData.combo || 0
    });
  }

  // Send buffered slashes to blockchain in a batch
  async sendSlashBatch() {
    // TODO: Implement batch slash recording
    // For now, skip this to focus on NFT minting
    console.log(' Skipping slash batch send (not implemented yet)');
    this.slashBuffer = []; // Clear buffer
    return null;
  }

  // Mint NFT with game results
  async mintGameNFT(gameStats) {
    if (!this.contract || !this.account) {
      throw new Error('Wallet not connected');
    }

    try {
      // Verify account is still connected and accessible
      console.log('🔍 Pre-mint account check:', {
        accountAddress: this.account.address,
        hasProvider: !!this.account.provider,
        hasSigner: !!this.account.signer,
        contractAddress: this.contractAddress
      });

      // Send any remaining slashes before minting
      if (this.slashBuffer.length > 0) {
        await this.sendSlashBatch();
      }

      console.log('🎨 Minting game NFT...');
      console.log('📊 Game stats:', gameStats);

      const gameDuration = this.gameStartTime 
        ? Math.floor((Date.now() - this.gameStartTime) / 1000)
        : 0;

      // Prepare call data
      const callData = {
        final_score: uint256.bnToUint256(gameStats.score || 0),
        max_combo: uint256.bnToUint256(gameStats.maxCombo || 0),
        tokens_sliced: uint256.bnToUint256(gameStats.tokensSlashed || 0),
        bombs_hit: uint256.bnToUint256(gameStats.bombsHit || 0),
        game_duration: gameDuration
      };

      console.log('Call data:', callData);
      console.log('Account details:', {
        address: this.account.address,
        cairoVersion: this.account.cairoVersion,
        chainId: this.account.chainId
      });

      // Use the contract's populate transaction and then execute
      // This ensures proper encoding for Argent X multi-sig
      const myCall = this.contract.populate('mint_game_nft', [
        callData.final_score,
        callData.max_combo,
        callData.tokens_sliced,
        callData.bombs_hit,
        callData.game_duration
      ]);

      console.log('Populated call:', myCall);

      // Execute the transaction using the account
      const result = await this.account.execute(myCall);

      console.log('Minting transaction sent:', result.transaction_hash);

      // Wait for confirmation
      const receipt = await this.provider.waitForTransaction(result.transaction_hash);
      
      console.log('Transaction receipt:', receipt);
      
      if (receipt.execution_status === 'SUCCEEDED' || receipt.status === 'ACCEPTED_ON_L2') {
        console.log('✅ NFT Minted successfully!');
        
        // Extract token ID from events
        const tokenId = this.extractTokenIdFromReceipt(receipt);
        
        return {
          success: true,
          tokenId,
          transactionHash: result.transaction_hash
        };
      } else {
        throw new Error('Transaction failed');
      }
    } catch (error) {
      console.error('❌ Failed to mint NFT:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        data: error.data,
        stack: error.stack
      });
      
      // User friendly error messages
      let errorMessage = error.message;
      if (error.message?.includes('cosigner')) {
        errorMessage = 'Account not deployed. Please send a self-transfer in Argent X to deploy your account first. Send 1 STRK to your own address: 0x05a0...adc0';
      } else if (error.message?.includes('User abort')) {
        errorMessage = 'Transaction was cancelled by user.';
      } else if (error.message?.includes('insufficient')) {
        errorMessage = 'Insufficient funds for transaction fee.';
      } else if (error.message?.includes('Contract not found') || error.message?.includes('not deployed')) {
        errorMessage = 'Account not deployed. Send 1 STRK to yourself in Argent X to deploy your account.';
      }
      
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // Get game session data from NFT
  async getGameSession(tokenId) {
    if (!this.contract) {
      throw new Error('Contract not initialized');
    }

    try {
      const session = await this.contract.get_game_session(
        uint256.bnToUint256(tokenId)
      );
      
      return {
        player: session.player,
        finalScore: uint256.uint256ToBN(session.final_score).toString(),
        maxCombo: uint256.uint256ToBN(session.max_combo).toString(),
        tokensSliced: uint256.uint256ToBN(session.tokens_sliced).toString(),
        bombsHit: uint256.uint256ToBN(session.bombs_hit).toString(),
        gameDuration: session.game_duration.toString(),
        timestamp: session.timestamp.toString()
      };
    } catch (error) {
      console.error('❌ Failed to get game session:', error);
      throw error;
    }
  }

  // Get user's NFT balance
  async getUserNFTCount(address) {
    if (!this.contract) {
      throw new Error('Contract not initialized');
    }

    try {
      const balance = await this.contract.balance_of(address);
      return uint256.uint256ToBN(balance).toString();
    } catch (error) {
      console.error('❌ Failed to get NFT balance:', error);
      throw error;
    }
  }

  // Helper to extract token ID from transaction receipt
  extractTokenIdFromReceipt(receipt) {
    try {
      // Look for NFTMinted event
      const events = receipt.events || [];
      const mintEvent = events.find(e => e.keys && e.keys[0] === 'NFTMinted');
      
      if (mintEvent && mintEvent.data) {
        // Token ID is usually in the event data
        return uint256.uint256ToBN(mintEvent.data[1]).toString();
      }
      
      return null;
    } catch (error) {
      console.error('Failed to extract token ID:', error);
      return null;
    }
  }

  // Get current wallet address
  getWalletAddress() {
    return this.account?.address || null;
  }

  // Check if wallet is connected
  isWalletConnected() {
    return !!this.account;
  }
}

// Export singleton instance
const starknetGameService = new StarknetGameService();
export default starknetGameService;
